import React, { useEffect, useState } from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip } from 'components/mui';
import { CopyIcon, ExpandLess, ExpandMore } from 'components/icons';
import { CANtrackComment } from '../../types/comment';
import { Chip, Typography } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import CaseCommentsTableRowExpanded from './CaseCommentsTableRowExpanded';
import { TimeEntryEditableCell } from '../table/inlineEditableCells/TimeEntryEditableCell';
import { TimeSpentEditableCell } from '../table/inlineEditableCells/TimeSpentEditableCell';

interface CaseCommentsTableRowProps {
	comment: CANtrackComment;
	decimalFormat: boolean;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
	onCopyCommentClick: (commentToCopy: CANtrackComment) => void;
	hideCaseInfo?: boolean;
}

// Adding an additional prop `caseUrl` for navigation.
const ReadMore: React.FC<{
	text: string;
	caseUrlV3: string;
	caseUrlV1: string;
}> = ({ text, caseUrlV1, caseUrlV3 }) => {
	const handleClick = (event: any) => {
		if (event.shiftKey && event.ctrlKey) {
			window.open(caseUrlV1, '_blank'); // open V1 URL if shift key is pressed while clicking
		} else {
			window.open(caseUrlV3, '_blank');
		}
	};

	return (
		<Box display="flex">
			<Tooltip title={text} placement="top" arrow>
				<Typography
					sx={{
						display: '-webkit-box',
						WebkitLineClamp: 1,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						cursor: 'pointer',
						textDecoration: 'none',
						'&:hover': {
							textDecoration: 'underline',
						},
					}}
					onClick={handleClick}
				>
					{text}
				</Typography>
			</Tooltip>
		</Box>
	);
};

const CaseCommentsTableRow: React.FC<CaseCommentsTableRowProps> = (props) => {
	const { comment, decimalFormat: showDecimalFormat, setComments, hideCaseInfo } = props;
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const handleRowClick = (e: React.MouseEvent) => {
		// Only toggle on mobile, and avoid toggling when clicking buttons/inputs
		const target = e.target as HTMLElement;
		if (target.closest('button') || target.closest('input')) return;
		setIsExpanded(!isExpanded);
	};

	return (
		<>
			<TableRow
				key={comment.id}
				hover
				sx={{
					'&:last-child td, &:last-child th': {
						border: 0,
					},
					cursor: 'pointer',
				}}
				onClick={handleRowClick}
			>
				<TableCell sx={{ width: '10px', display: { xs: 'none', md: 'table-cell' } }} align="left">
					<IconButton onClick={() => setIsExpanded(!isExpanded)}>
						{isExpanded ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				</TableCell>
				{!hideCaseInfo && (
					<TableCell
						sx={{ width: '10px', pl: '0px', display: { xs: 'none', md: 'table-cell' } }}
						component="th"
						scope="row"
					>
						<Chip label={comment.customerName} variant="outlined" />
					</TableCell>
				)}
				{!hideCaseInfo && (
					<TableCell sx={{ width: '300px', display: { xs: 'none', md: 'table-cell' } }} component="th" scope="row">
						<ReadMore
							text={comment.caseTitle}
							caseUrlV3={`/cantrack/cases/${comment.caseID}`}
							caseUrlV1={`/Portal/Project/EditCase.aspx?tabid=9&caseid=${comment.caseID}`}
						/>
					</TableCell>
				)}
				<TableCell align="left">
					<Box sx={{ minWidth: { xs: 'unset', md: '300px' } }}>{comment.title}</Box>
				</TableCell>
				<TimeEntryEditableCell
					comment={comment}
					setComments={setComments}
					sx={{ display: { xs: 'none', md: 'table-cell' } }}
				/>
				<TimeSpentEditableCell
					comment={comment}
					setComments={setComments}
					showDecimalFormat={showDecimalFormat}
				/>
				<TableCell sx={{ width: '10px', display: { xs: 'none', md: 'table-cell' } }} align="center">
					<Tooltip title={comment.billable ? 'Billable' : 'Non-billable'}>
						{comment.billable ? (
							<AttachMoneyIcon color="success" fontSize="small" />
						) : (
							<MoneyOffIcon color="disabled" fontSize="small" />
						)}
					</Tooltip>
				</TableCell>
				<TableCell sx={{ width: '10px', display: {md: 'table-cell' } }} align="right">
					<Tooltip title="Copy Comment">
						<IconButton
							onClick={() => props.onCopyCommentClick(comment)}
						>
							<CopyIcon />
						</IconButton>
					</Tooltip>
				</TableCell>
			</TableRow>
			{isExpanded && (
				<CaseCommentsTableRowExpanded
					comment={comment}
					isEditing={isEditing}
					setIsEditing={setIsEditing}
					setComments={setComments}
				/>
			)}
		</>
	);
};

export default CaseCommentsTableRow;
